import { useState } from "react";
import { useSaveVisitTimes } from "../../api/inspections";

// Johannesburg keeps no daylight saving, so the office's offset is fixed.
const OFFICE_ZONE = "Africa/Johannesburg";
const OFFICE_OFFSET = "+02:00";

/** "08:52" on the office clock, from a stored instant. */
export function officeClock(iso: string | null | undefined): string {
  if (!iso) {
    return "";
  }
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: OFFICE_ZONE, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(iso));
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
  return `${part("hour")}:${part("minute")}`;
}

/** The instant a clock time names on the visit's day, on the office clock. */
export function officeInstant(day: string, time: string): string | null {
  return time === "" ? null : `${day}T${time}:00${OFFICE_OFFSET}`;
}

/** When the field operators arrived at the chamber and left it, on a PM41
 * visit: the same visit-header fields a PM42 visit records, so the plan can
 * show planned beside actual for either form. Saved as each is left. */
export default function VisitTimes({
  inspectionId,
  day,
  arrivedAt,
  departedAt,
  canEdit,
}: {
  inspectionId: number;
  day: string;
  arrivedAt: string | null;
  departedAt: string | null;
  canEdit: boolean;
}) {
  const save = useSaveVisitTimes(inspectionId);
  const [arrived, setArrived] = useState(officeClock(arrivedAt));
  const [left, setLeft] = useState(officeClock(departedAt));
  const [saved, setSaved] = useState({ arrived: officeClock(arrivedAt), left: officeClock(departedAt) });

  function commit() {
    if (arrived === saved.arrived && left === saved.left) {
      return;
    }
    save.mutate(
      { arrived_at: officeInstant(day, arrived), departed_at: officeInstant(day, left) },
      { onSuccess: () => setSaved({ arrived, left }) },
    );
  }

  return (
    <section className="insp-subsection" aria-labelledby="visit-times-label">
      <h4 id="visit-times-label">When the field operators arrived and left</h4>
      <div className="insp-fields">
        <label>
          Arrived
          <input type="time" value={arrived} readOnly={!canEdit} onChange={(e) => setArrived(e.target.value)} onBlur={commit} />
        </label>
        <label>
          Left
          <input type="time" value={left} readOnly={!canEdit} onChange={(e) => setLeft(e.target.value)} onBlur={commit} />
        </label>
      </div>
      {save.isError ? <p className="notice bad">Not saved: {save.error.message}</p> : null}
    </section>
  );
}
